import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import SeoHead from '../seo/SeoHead';
import { staticPageMeta } from '../seo/metadata';

const Subscriptions = () => {
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    const fetchSubscriptions = async () => {
      try {
        const params = filter !== 'all' ? `?type=${filter}` : '';
        const response = await fetch(`/api/sectrace/subscribe${params}`);
        const data = await response.json();
        setSubscriptions(data);
      } catch (error) {
        console.error('Failed to fetch subscriptions:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchSubscriptions();
  }, [filter]);

  const formatDate = (timestamp) => {
    if (!timestamp) return '-';
    const date = new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const alertTypeLabels = {
    'activist_alerts': 'Activist Alerts',
    'portfolio': 'Portfolio Watch',
    'ir_watch': 'IR Watch',
    'reporter': 'Reporter Alerts'
  };

  return (
    <div className="min-h-screen bg-white">
      <SeoHead
        {...staticPageMeta({
          title: 'Subscriptions',
          description: 'Phalanx email subscriptions.',
          path: '/subscriptions',
          robots: 'noindex, nofollow',
        })}
      />
      {/* Header */}
      <header className="border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <nav className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/" className="text-gray-400 hover:text-gray-600 text-sm">← Phalanx</Link>
              <span className="text-gray-900 font-medium">Subscriptions</span>
            </div>
          </nav>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <div className="mb-6">
          <h1 className="text-2xl font-light text-gray-900 mb-2">Email Subscriptions</h1>
          <p className="text-gray-500 text-sm">All subscriber emails and their subscription details</p>
        </div>

        {/* Filter */}
        <div className="mb-6 flex items-center gap-2">
          <span className="text-sm text-gray-500">Filter:</span>
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="px-3 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-gray-900"
          >
            <option value="all">All Types</option>
            <option value="activist_alerts">Activist Alerts</option>
            <option value="portfolio">Portfolio Watch</option>
            <option value="ir_watch">IR Watch</option>
            <option value="reporter">Reporter Alerts</option>
          </select>
          <span className="text-sm text-gray-400 ml-4">
            {subscriptions.length} subscription{subscriptions.length !== 1 ? 's' : ''}
          </span>
        </div>

        {/* Table */}
        {loading ? (
          <div className="text-center py-12 text-gray-500">Loading...</div>
        ) : subscriptions.length === 0 ? (
          <div className="text-center py-12 text-gray-500">No subscriptions found</div>
        ) : (
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Unix Time</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Companies</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {subscriptions.map((sub) => (
                    <tr key={sub.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-900 font-medium">
                        {sub.email}
                      </td>
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700">
                          {alertTypeLabels[sub.alert_type] || sub.alert_type}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                          sub.is_active 
                            ? 'bg-green-100 text-green-700' 
                            : 'bg-red-100 text-red-700'
                        }`}>
                          {sub.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500">
                        {formatDate(sub.created_at)}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-400 font-mono">
                        {sub.created_unix || '-'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500">
                        {sub.companies && sub.companies.length > 0 
                          ? sub.companies.slice(0, 3).join(', ') + (sub.companies.length > 3 ? '...' : '')
                          : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="py-6 px-4 sm:px-6 border-t border-gray-100 mt-auto">
        <div className="max-w-6xl mx-auto text-center text-xs text-gray-500">
          &copy; 2026 Northlight Technologies
        </div>
      </footer>
    </div>
  );
};

export default Subscriptions;
